import { Box, Link, Typography } from '@mui/material';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/case-studies/rising-routes-alliance';
const PAGE_IMAGE = 'https://www.rsmc.ai/images/rising-routes-maroon-lake.jpg';
const PAGE_DESCRIPTION =
  'Every funding decision, policy conversation, and resource allocation starts with data. Learn how one nonprofit is using AI to help drive outdoor equity & knowledge for providers, policy makers and funders.';
const VIDEO_URL = 'https://www.youtube.com/watch?v=hc900yelIEY';
// Privacy-enhanced embed; nothing is loaded from YouTube until the still is clicked.
const VIDEO_EMBED_URL =
  'https://www.youtube-nocookie.com/embed/hc900yelIEY?autoplay=1&rel=0';

/**
 * In-page player for the Rising Routes Alliance video. Renders the video still
 * with a play button; clicking it swaps in the YouTube embed and starts
 * playback, so no YouTube assets load until the visitor asks for them.
 *
 * @returns {import('react').ReactElement} The video player
 */
function VideoPlayer() {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16 / 9',
          borderRadius: '12px',
          overflow: 'hidden',
          mb: 2,
          backgroundColor: '#000000',
        }}
      >
        <Box
          component="iframe"
          src={VIDEO_EMBED_URL}
          title="Jason Swann, Executive Director of Rising Routes Alliance, on the PEAK Outdoor Intelligence Platform"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={() => setPlaying(true)}
      aria-label="Play the video: Jason Swann of Rising Routes Alliance on why this work matters"
      sx={{
        display: 'block',
        position: 'relative',
        width: '100%',
        p: 0,
        border: 0,
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 2,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        '&:hover .play-button, &:focus-visible .play-button': {
          backgroundColor: 'var(--color-primary)',
        },
      }}
    >
      <Box
        component="img"
        src="/images/rising-routes-video-still.jpg"
        alt="Still from the Rising Routes Alliance video, a river winding through a forested Colorado landscape, overlaid with the words: Challenge: Outdoor access providers are not always seen by the systems meant to support them."
        sx={{ display: 'block', width: '100%', height: 'auto' }}
      />
      <Box
        className="play-button"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          border: '2px solid var(--color-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s',
        }}
      >
        <Box
          component="span"
          sx={{
            width: 0,
            height: 0,
            borderTop: '12px solid transparent',
            borderBottom: '12px solid transparent',
            borderLeft: '20px solid #ffffff',
            ml: '4px',
          }}
        />
      </Box>
    </Box>
  );
}

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  mt: 6,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

/**
 * Open typographic pull quote: left-aligned with a large decorative quote
 * mark, indented on desktop, no container. Deliberately lighter than the
 * boxed treatment so it reads as a breather between the feature grid and the
 * video player rather than a third box in a row.
 *
 * @param {Object} props - Component props
 * @param {string} props.quote - The quoted text
 * @returns {import('react').ReactElement} A pull quote
 */
function PullQuote({ quote }) {
  return (
    <Box
      component="figure"
      sx={{
        my: { xs: 6, md: 8 },
        mx: 0,
        pl: { xs: 1, md: 6 },
        pr: { xs: 1, md: 2 },
      }}
    >
      <Typography
        component="span"
        aria-hidden="true"
        sx={{
          display: 'block',
          fontFamily: '"Playfair Display", serif',
          fontSize: '4rem',
          lineHeight: 0.6,
          color: 'var(--color-primary)',
          mb: 2,
        }}
      >
        &ldquo;
      </Typography>
      <Typography
        component="blockquote"
        sx={{
          fontSize: { xs: '1.25rem', md: '1.5rem' },
          lineHeight: 1.5,
          fontWeight: 600,
          color: 'var(--color-heading)',
          fontStyle: 'italic',
          m: 0,
        }}
      >
        {quote}
      </Typography>
    </Box>
  );
}

const platformFeatures = [
  {
    number: '1',
    heading: 'Single trusted repository',
    body: 'One source of outdoor access data across the entire state of Colorado.',
  },
  {
    number: '2',
    heading: 'Consolidated datasets',
    body: "Existing data brought together so providers don't start from zero.",
  },
  {
    number: '3',
    heading: 'Shared statewide view',
    body: 'A unified perspective for policymakers, funders, and communities.',
  },
  {
    number: '4',
    heading: 'Provider-shaped',
    body: 'Built around the organizations already doing this critical work.',
  },
];

/**
 * Rising Routes Alliance case study
 *
 * "AI For Good" story covering the partnership between Rising Routes Alliance
 * and RSMC to build the PEAK Outdoor Intelligence Platform. Uses
 * StoryPageLayout so the AWS-funded POC form sits in a sticky sidebar beside
 * the content, matching the HubLife for Nonprofits solution guide.
 *
 * @returns {import('react').ReactElement} Rising Routes Alliance case study page
 */
function RisingRoutesCaseStudy() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline:
      'One Nonprofit Turned Disconnected Data into an Agentic AI Platform for Outdoor Providers',
    description: PAGE_DESCRIPTION,
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    image: PAGE_IMAGE,
    datePublished: '2026-08-26',
    dateModified: '2026-08-26',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>
          AI For Good: Rising Routes Alliance & the PEAK Outdoor Intelligence
          Platform | RSMC
        </title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          property="og:title"
          content="One Nonprofit Turned Disconnected Data into an Agentic AI Platform for Outdoor Providers"
        />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        <meta
          name="twitter:title"
          content="AI For Good: Rising Routes Alliance Case Study"
        />
        <meta
          name="twitter:description"
          content="How Rising Routes Alliance and RSMC are building the PEAK Outdoor Intelligence Platform for outdoor equity in Colorado."
        />
        <meta
          name="keywords"
          content="Rising Routes Alliance, outdoor equity, PEAK Outdoor Intelligence Platform, agentic AI, nonprofit technology, AWS partner, Colorado"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="AI For Good"
        subtitle="A Rising Routes Alliance Case Study"
        formHeading="Let's Connect"
        formFilePath="/forms/aws-funded-poc-form.txt"
        heroImage="/images/rising-routes-maroon-lake.jpg"
        heroImageAlt="Maroon Lake with the Maroon Bells rising in the background, Aspen, Colorado"
        errorMessage="Unable to load contact form"
        loadingMessage="Loading contact form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            One Nonprofit Turned Disconnected Data into an Agentic AI Platform
            for Outdoor Providers
          </Typography>

          {/* Editorial meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 4,
            }}
          >
            {['Rising Routes Alliance', 'Colorado', '2026'].map(
              (item, index) => (
                <Box
                  key={item}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  {index > 0 && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary)',
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              )
            )}
          </Box>

          {/* Deck */}
          <Typography
            sx={{
              ...paragraphSx,
              fontSize: '1.125rem',
              color: 'var(--color-text-muted)',
              mb: 4,
            }}
          >
            Outdoor equity gets talked about a lot. But what does it actually
            look like in practice?
          </Typography>

          <Typography sx={paragraphSx}>
            It means a provider in the Eastern Plains of Colorado has access to
            the same statewide data and funding pathways as a large Denver-based
            organization. It means underserved communities are part of the
            policy conversations that shape their access to the outdoors, not
            just the recipients of decisions made without them.
          </Typography>

          <Typography sx={paragraphSx}>
            Fragmented data is what makes outdoor equity so hard to see, and
            even harder to fund. It leaves policymakers, funders, and providers
            each working from a limited view of the outdoor access landscape.
            Closing that gap starts with better data.
          </Typography>

          <Typography sx={paragraphSx}>
            This is what Rising Routes is building toward &mdash; not just
            access to the outdoors, but access to the systems that make outdoor
            programming possible in the first place.
          </Typography>

          {/* The platform */}
          <Typography variant="h2" sx={headingSx}>
            Introducing the PEAK Outdoor Intelligence Platform
          </Typography>

          <Typography sx={paragraphSx}>
            To advance that work, Rising Routes has partnered with{' '}
            <strong>Ready Secure Managed Cloud (RSMC)</strong> to build the PEAK
            Outdoor Intelligence Platform: a clean, accurate data repository
            that consolidates existing sources into a trusted, statewide view of
            the outdoor access landscape. One that policymakers, funders, and
            communities can rely on.
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: '1.0625rem',
              color: 'var(--color-heading)',
              mt: 4,
              mb: 2,
            }}
          >
            What the platform brings together
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {platformFeatures.map((item) => (
              <Box
                key={item.number}
                sx={{
                  border: '1px solid var(--color-border)',
                  borderRadius: '12px',
                  backgroundColor: 'var(--color-card)',
                  p: 2.5,
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    backgroundColor:
                      'color-mix(in srgb, var(--color-primary) 15%, transparent)',
                    color: 'var(--color-primary)',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    mb: 1.5,
                  }}
                >
                  {item.number}
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: 'var(--color-heading)',
                    mb: 1,
                  }}
                >
                  {item.heading}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    lineHeight: 1.7,
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {item.body}
                </Typography>
              </Box>
            ))}
          </Box>

          <PullQuote quote="We are approaching this work with the same standard we hold for everything we build. Technology should support the people and organizations doing this work, not replace them." />

          {/* Video */}
          <Typography variant="h2" sx={headingSx}>
            Hear from the Executive Director
          </Typography>

          <Typography sx={paragraphSx}>
            <strong>This work is now underway.</strong> Watch Jason Swann,
            Executive Director of Rising Routes Alliance, share why this work
            matters and what&rsquo;s next.
          </Typography>

          <VideoPlayer />

          <Typography
            sx={{
              fontSize: '0.875rem',
              color: 'var(--color-text-muted)',
              mb: 3,
            }}
          >
            <Link
              href={VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'var(--color-primary)',
                textDecoration: 'underline',
              }}
            >
              Watch Jason Swann on YouTube
            </Link>{' '}
            &mdash; Executive Director, Rising Routes Alliance
          </Typography>

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default RisingRoutesCaseStudy;
